import * as React from 'react'
import { Command, CommandInput, CommandItem, CommandList, CommandSeparator } from '../command'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { useContentWidth } from './use-content-width'
import { ComboboxLoading, ComboboxTrigger } from './common'
import { cn } from '@/lib/utils'
import { Button } from '../button'
import { Input } from '../input'
import { isBefore } from 'date-fns'
import { toast } from 'sonner'
import type { Option } from '../cascader/single'
import { HighlightText } from '../highlight-text'

interface ComboboxValueProps {
  value?: string
  onValueChange?: (value: string | undefined, options?: Option[]) => void
  setOpen?: (open: boolean) => void
  search?: string
}

export const ComboboxContext = React.createContext<ComboboxValueProps>({})

export interface ComboboxProps extends ComboboxValueProps {
  allowClear?: boolean
  placeholder?: string
  defaultValue?: string
  showSearch?: boolean
  searchPlaceholder?: string
  className?: string
  outClassName?: string
  style?: React.CSSProperties
  searchKeyword?: string
  onSearch?: (keyword: string) => void
  onClose?: () => void
  children?: React.ReactNode
  shouldFilter?: boolean
  listClassName?: string
  customPopoverWidth?: boolean
  popoverClassName?: string
  commondClassName?: string
  customDownIcon?: React.ReactNode
  variant?: 'default' | 'outline'
  disabled?: boolean
  operateChildren?: React.ReactNode
  activeLight?: boolean
  showLabel?: boolean
  customDisplay?: React.ReactNode
}

export type ComboboxRangeInputType = {
  value?: [string | number, string | number]
  inline?: boolean
  prefix?: React.ReactNode
  unit?: string
  placeholder?: [string, string]
  className?: string
  inputClassName?: string
  confirmClassName?: string
  onValueChange?: (value: [string, string], options?: Option[]) => void
  allowClear?: boolean
  alwaysShowButtons?: boolean
  min?: number
  max?: number
  btnClassName?: string
  blurConfirm?: boolean
}

function ComboboxRangeInput({
  onValueChange,
  placeholder = ['', ''],
  prefix,
  unit,
  value = ['', ''],
  inline,
  className,
  inputClassName,
  confirmClassName,
  allowClear = true,
  alwaysShowButtons,
  max: maxProp,
  min: minProp = 0,
  btnClassName,
  blurConfirm = false,
}: ComboboxRangeInputType) {
  const { setOpen } = React.useContext(ComboboxContext)
  const [min, setMin] = React.useState<string>(String(value[0]))
  const [max, setMax] = React.useState<string>(String(value[1]))
  const [showButtons, setShowButtons] = React.useState(false)
  const [minPlaceholder, maxPlaceholder] = placeholder
  const handleMinChange = (val: string) => {
    // 不允许负数
    const newVal = val.startsWith('-') ? '' : val
    setMin(newVal)
  }

  const handleMaxChange = (val: string) => {
    // 不允许负数
    const newVal = val.startsWith('-') ? '' : val
    setMax(newVal)
  }

  React.useEffect(() => {
    setMin(String(value[0] || ''))
    setMax(String(value[1] || ''))
  }, [JSON.stringify(value)])

  const onConfirm = () => {
    // 如果最小值或最大值为空，则不进行交换
    if (!min || !max) {
      const newValue: [string, string] = [min || '', max || '']
      onValueChange?.(newValue)
      setOpen?.(false)
      return
    }
    const minNum = Number(min)
    const maxNum = Number(max)
    const newValue: [string, string] = minNum > maxNum ? [max, min] : [min, max]
    onValueChange?.(newValue)
    setOpen?.(false)
  }

  return (
    <div
      className={cn('sticky bottom-0 bg-background px-2', { 'flex items-center': inline }, className)}
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
    >
      <div className="flex items-center gap-x-1">
        <Input
          key={'minInput'}
          min={minProp}
          max={maxProp}
          placeholder={minPlaceholder}
          onKeyDown={(ev) => {
            ev.stopPropagation()
          }}
          className={cn('pointer-events-auto h-6 w-14.5 rounded-none bg-background px-0', inputClassName)}
          value={min}
          onValueChange={handleMinChange}
          type="number"
          onBlur={() => {
            if (blurConfirm) {
              onConfirm()
            }
          }}
        >
          <Input.Prefix>{prefix}</Input.Prefix>
          <Input.Suffix>{unit}</Input.Suffix>
        </Input>
        <span>~</span>
        <Input
          key={'maxInput'}
          min={minProp}
          max={maxProp}
          placeholder={maxPlaceholder}
          onKeyDown={(ev) => {
            ev.stopPropagation()
          }}
          className={cn('h-6 w-14.5 rounded-none bg-background px-0', inputClassName)}
          value={max}
          onValueChange={handleMaxChange}
          type="number"
          onBlur={() => {
            if (blurConfirm) {
              onConfirm()
            }
          }}
        >
          <Input.Prefix>{prefix}</Input.Prefix>
          <Input.Suffix>{unit}</Input.Suffix>
        </Input>
      </div>
      {(allowClear || !blurConfirm) && (
        <div
          className={cn('mt-2 flex items-center justify-between transition-opacity duration-200 gap-2', {
            'mt-0 ml-2': inline,
            'opacity-0': inline && !showButtons && !alwaysShowButtons,
          })}
        >
          {allowClear ? (
            <Button
              className={cn('w-20 h-8 mr-1', btnClassName)}
              type="button"
              variant={'secondary'}
              size={'sm'}
              onClick={() => {
                onValueChange?.([] as unknown as [string, string])
                setOpen?.(false)
              }}
            >
              清空
            </Button>
          ) : (
            <></>
          )}

          {!blurConfirm && (
            <Button className={cn('w-20 h-8', confirmClassName)} type="button" size={'sm'} variant="default" onClick={onConfirm}>
              确定
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export type ComboboxOptionType = {
  value: string
  disabled?: boolean
  keywords?: string[]
  children?: React.ReactNode
  className?: string
}
export type ComboboxOptionProp = ComboboxOptionType & ComboboxValueProps

function ComboboxOption({ children, value, disabled, keywords, className }: ComboboxOptionProp) {
  const { value: selectedValue, onValueChange, setOpen, search } = React.useContext(ComboboxContext)

  return (
    <CommandItem
      disabled={disabled}
      key={value}
      value={value}
      className={cn(
        'data-[disabled=true]:text-muted-foreground data-[disabled=true]:opacity-100',
        { 'bg-accent-select text-accent hover:bg-accent-select hover:text-accent': value === selectedValue },
        className,
      )}
      keywords={keywords}
      onSelect={(currentValue) => {
        onValueChange?.(currentValue)
        setOpen?.(false)
      }}
    >
      <>{typeof children === 'string' ? <HighlightText text={children} highlight={search ? [search] : []} /> : children}</>
    </CommandItem>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ComboboxDisplay(_: { children?: React.ReactNode | null }) {
  return null
}
ComboboxDisplay.displayName = 'ComboboxDisplay'

function formatDateStr(string: string) {
  const cleanValue = string.replaceAll(/\D/g, '')
  return `${cleanValue.slice(0, 4)}-${cleanValue.slice(4, 6)}-${cleanValue.slice(6)}`
}

export function defaultFormat(value: string[]) {
  const regex = /^\d{4}.?\d{2}.?\d{2}$/
  if (!regex.test(value[0]) || !regex.test(value[1])) {
    return 'format-error'
  }
  if (isBefore(new Date(formatDateStr(value[1])), new Date(formatDateStr(value[0])))) {
    return 'range-error'
  }
  return true
}

export function formatDateValue(value: string[]) {
  const dateValue = value.map((m) => formatDateStr(m))
  return dateValue.join(',')
}

interface ComboboxIntervalProps {
  placeholder?: string
  format?: (val: string[]) => boolean | string
}
function ComboboxInterval({ format = defaultFormat }: ComboboxIntervalProps) {
  const { value: selected, onValueChange, setOpen } = React.useContext(ComboboxContext)
  const [value, setValue] = React.useState<string[]>([])

  React.useEffect(() => {
    if (!selected) {
      setValue([])
      return
    }

    const newValue = typeof selected === 'string' ? selected.split(',') : []
    setValue(newValue)
  }, [selected])

  return (
    <Button
      variant="secondary"
      className="w-15 cursor-pointer bg-accent text-background hover:bg-accent/90"
      onClick={() => {
        if (!value.length) return setOpen?.(false)
        const formatResult = format?.(value)
        if (formatResult === 'format-error') {
          return toast('日期格式错误，正确格式为yyyy-MM-dd')
        }
        if (formatResult === 'range-error') {
          return toast('开始日期不能大于结束日期')
        }
        if (formatResult !== true) {
          return toast('日期不合法')
        }
        const v = formatDateValue(value)
        onValueChange?.(v)
        setOpen?.(false)
      }}
    >
      确定
    </Button>
  )
}

function Combobox({
  className,
  outClassName,
  placeholder,
  defaultValue,
  searchPlaceholder = '搜索',
  showSearch = true,
  value,
  allowClear = true,
  onValueChange,
  onSearch,
  onClose,
  children,
  shouldFilter,
  listClassName,
  popoverClassName,
  customPopoverWidth = false,
  commondClassName,
  customDownIcon,
  variant,
  disabled = false,
  operateChildren,
  activeLight = false,
  showLabel = false,
  customDisplay,
}: ComboboxProps) {
  const val = value ?? defaultValue
  const [open, setOpen] = React.useState<boolean>(false)
  const [composing, setComposing] = React.useState(false)
  const [display, setDisplay] = React.useState<string>()
  const [search, setSearch] = React.useState<string>('')
  const $trigger = React.useRef<HTMLButtonElement>(null)
  const contentWidth = useContentWidth($trigger as React.RefObject<HTMLElement>)
  const context = { value: val, onValueChange, setOpen, search }
  const $list = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!showLabel && activeLight) {
      return setDisplay(placeholder || '不限')
    }
    let found = false
    React.Children.forEach(children, (n) => {
      const m = n as React.ReactElement
      if (!m?.props || found) return
      if (m.props.value === val) {
        const label = m.props?.label ?? m.props?.children
        if (placeholder) {
          setDisplay(placeholder + (label ? ' (' + label + ')' : ''))
        } else {
          // 暂时使用硬编码
          setDisplay(label || '不限')
        }
        found = true
        return
      }
      if (m.type && (m.type as React.NamedExoticComponent).displayName === 'ComboboxDisplay' && m.props.children) {
        setDisplay(m.props.children)
        found = true
      }
    })

    if (!found) {
      setDisplay(placeholder || '不限')
    }
    setTimeout(() => {
      if (!$list.current) return
      $list.current.scrollTop = 0
    }, 0)
  }, [val, children, placeholder])

  return (
    <ComboboxContext.Provider value={context}>
      <Popover
        open={open}
        onOpenChange={(open) => {
          if (disabled) return
          if (!open) onClose?.()
          setOpen(open)
        }}
      >
        <span className={cn('relative', outClassName)}>
          <PopoverTrigger asChild>
            <ComboboxTrigger
              disabled={disabled}
              className={className}
              ref={$trigger}
              role="combobox"
              onClear={() => {
                onValueChange?.('')
              }}
              allowClear={allowClear}
              selected={!!val}
              activeLight={activeLight}
              aria-expanded={open}
              customDownIcon={customDownIcon}
              variant={variant}
            >
              {customDisplay ? customDisplay : display}
            </ComboboxTrigger>
          </PopoverTrigger>
        </span>
        <PopoverContent
          align="start"
          className={cn('min-w-40 p-0', popoverClassName)}
          style={customPopoverWidth ? {} : { minWidth: contentWidth }}
        >
          <Command shouldFilter={shouldFilter} className={cn('p-2', commondClassName)} value={val}>
            <div className={showSearch ? 'mb-2' : ''}>
              {!!showSearch && (
                <CommandInput
                  placeholder={searchPlaceholder}
                  className="h-9"
                  value={search}
                  onValueChange={(v) => {
                    setSearch(v)
                    if ($list.current) {
                      $list.current.scrollTop = 0
                    }
                    if (composing) return
                    onSearch?.(v)
                  }}
                  onCompositionStart={() => {
                    setComposing(true)
                  }}
                  onCompositionEnd={(ev) => {
                    setComposing(false)
                    onSearch?.((ev.target as HTMLInputElement).value)
                  }}
                />
              )}
            </div>
            <CommandList ref={$list} className={listClassName}>
              <React.Suspense fallback={<ComboboxLoading />}>{children}</React.Suspense>
            </CommandList>
          </Command>
          {operateChildren}
        </PopoverContent>
      </Popover>
    </ComboboxContext.Provider>
  )
}
Combobox.Separator = CommandSeparator
Combobox.Option = ComboboxOption
Combobox.Display = ComboboxDisplay
Combobox.RangeInput = ComboboxRangeInput
Combobox.Interval = ComboboxInterval
export default Combobox
